import { useEffect, useMemo, useRef } from "react";

import { AnalyticsContext, AnalyticsView, IssueEvent, trackEvent } from "#app/lib/analytics";
import type { UseFiltersResult } from "./types";
import { isCustomFieldFilterField } from "./custom-field-filter-fields";

/**
 * `issue.apply_filter` for a screen that holds its filters in `useFilters`
 * alone (the map), with no `IssueFilterProvider` to send it. The same rules
 * as the provider: one event per change of the active count or category set,
 * never on mount, never on a clear to empty.
 */
export function useTrackFiltersApplied(filters: UseFiltersResult, view: AnalyticsView): void {
  const { filterState, advancedState, activeFilterCount } = filters;
  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          [...filterState.blocks, ...advancedState.blocks]
            .flatMap((b) => b.filters.map((f) => f.field))
            .filter(Boolean)
            .map((f) => (isCustomFieldFilterField(f) ? "customField" : f)),
        ),
      ).sort(),
    [filterState, advancedState],
  );
  const signature = `${activeFilterCount}|${categories.join(",")}`;
  const prevSignature = useRef<string | null>(null);
  const advanced = advancedState.blocks.some((block) => block.filters.length > 0);

  useEffect(() => {
    if (prevSignature.current === null) {
      prevSignature.current = signature;
      return;
    }
    if (prevSignature.current === signature) return;
    prevSignature.current = signature;
    if (activeFilterCount === 0) return;
    trackEvent({
      context: AnalyticsContext.Issue,
      event: IssueEvent.ApplyFilter,
      data: { count: activeFilterCount, categories, advanced, view },
    });
    // The signature holds the count and the categories.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
}
